import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { crossBorderOrderService } from "../../../service/admin/crossBorderOrderService";
import { ProductsApiService } from "../../../service/website/productsApiService";
import { orderApiService } from "../../../service/admin/ordersApiService";
import { FaFileAlt, FaCreditCard, FaBox, FaTruck, FaCheckCircle, FaArrowLeft, FaSync, FaStore, FaBoxOpen, FaEye } from "react-icons/fa";
import ExcelTable from "../../../component/library/excelTable";
import "./style.css";

export default function CrossBorderOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const loader = useLoader();
  const message = useMessage();

  const [order, setOrder] = useState(null);
  const [originalOrders, setOriginalOrders] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isFetchingLogistics, setIsFetchingLogistics] = useState(false);
  const [isFetchingTrace, setIsFetchingTrace] = useState(false);
  const [activeTab, setActiveTab] = useState("importerr");
  const [translatedNames, setTranslatedNames] = useState({});
  const [translatedProducts, setTranslatedProducts] = useState({});
  const [translatedStatuses, setTranslatedStatuses] = useState({});

  useEffect(() => {
    if (id) fetchOrderDetail();
  }, [id]);

  const fetchOrderDetail = async () => {
    try {
      loader.showInlineLoader("Loading order details...", "small");
      const res = await crossBorderOrderService.getCrossBorderOrderById(id);
      if (res.status?.code === 200) {
        setOrder(res.data.order);
        setOriginalOrders(res.data.originalOrders || []);
        // Translate supplier name
        translateSupplierName(res.data.order?.supplierName);
        // Translate product names
        translateProductNames(res.data.order);
        // Translate status texts
        translateStatusTexts(res.data.order);
      }
    } catch (error) {
      message.showToast(error?.message || "Failed to load order", ToastTypes.ERROR);
    } finally {
      loader.hideLoader();
    }
  };

  const translateSupplierName = async (name) => {
    if (!name || !/[\u4e00-\u9fa5]/.test(name)) return;
    try {
      const translateData = { supplierNames: { [name]: name } };
      const res = await ProductsApiService.translateMandarinToEnglish(translateData, 'en');
      if (res.status?.code === 200 && res.data?.supplierNames) {
        setTranslatedNames(res.data.supplierNames);
      }
    } catch (error) {
      console.error("Translation error:", error);
    }
  };

  const translateProductNames = async (orderData) => {
    const chineseNames = {};
    
    // Get product names from buyerOrderDetail
    const productItemsList = orderData?.buyerOrderDetail?.[0]?.result?.productItems || [];
    productItemsList.forEach(item => {
      if (item.name && /[\u4e00-\u9fa5]/.test(item.name)) {
        chineseNames[item.name] = item.name;
      }
      // Also get variant names (skuInfos)
      const variantName = item.skuInfos?.map(s => `${s.name}: ${s.value}`).join(', ');
      if (variantName && /[\u4e00-\u9fa5]/.test(variantName)) {
        chineseNames[variantName] = variantName;
      }
    });
    
    // Get product names from items
    orderData?.items?.forEach(item => {
      if (item.productName && /[\u4e00-\u9fa5]/.test(item.productName)) {
        chineseNames[item.productName] = item.productName;
      }
    });
    
    if (Object.keys(chineseNames).length === 0) return;
    
    try {
      const translateData = { supplierNames: chineseNames };
      const res = await ProductsApiService.translateMandarinToEnglish(translateData, 'en');
      if (res.status?.code === 200 && res.data?.supplierNames) {
        setTranslatedProducts(res.data.supplierNames);
      }
    } catch (error) {
      console.error("Product translation error:", error);
    }
  };

  const translateStatusTexts = async (orderData) => {
    const chineseStatuses = {};
    
    // Known status mappings - no need to translate these
    const knownStatuses = {
      'waitbuyerpay': true, 'waitsellersend': true, 'waitbuyerreceive': true,
      'success': true, 'completed': true, 'cancel': true, 'pending': true,
    };
    
    // Get status texts from buyerOrderDetail
    const productItems = orderData?.buyerOrderDetail?.[0]?.result?.productItems || [];
    productItems.forEach(item => {
      const statusText = item.statusStr || item.status || "";
      if (statusText && /[\u4e00-\u9fa5]/.test(statusText) && !knownStatuses[statusText.toLowerCase()]) {
        chineseStatuses[statusText] = statusText;
      }
    });
    
    if (Object.keys(chineseStatuses).length === 0) return;
    
    try {
      const translateData = { supplierNames: chineseStatuses };
      const res = await ProductsApiService.translateMandarinToEnglish(translateData, 'en');
      if (res.status?.code === 200 && res.data?.supplierNames) {
        setTranslatedStatuses(res.data.supplierNames);
      }
    } catch (error) {
      console.error("Status translation error:", error);
    }
  };

  const handleRefreshDetails = async () => {
    if (!order?.alibabaOrderIds?.length) {
      message.showToast("No GLobal order IDs to fetch", ToastTypes.WARNING);
      return;
    }
    setIsRefreshing(true);
    try {
      const res = await crossBorderOrderService.fetchOrderDetails(id);
      if (res.status?.code === 200) {
        message.showToast("Order details refreshed", ToastTypes.SUCCESS);
        setOrder(res.data.order);
      }
    } catch (error) {
      message.showToast(error?.message || "Failed to refresh", ToastTypes.ERROR);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleFetchLogisticsInfo = async () => {
    if (!order?.alibabaOrderIds?.length) {
      message.showToast("No Global order IDs to fetch logistics", ToastTypes.WARNING);
      return;
    }
    setIsFetchingLogistics(true);
    try {
      const res = await crossBorderOrderService.fetchLogisticsInfo(id);
      if (res.status?.code === 200) {
        message.showToast("Logistics info fetched successfully", ToastTypes.SUCCESS);
        setOrder(res.data.order);
      }
    } catch (error) {
      message.showToast(error?.message || "Failed to fetch logistics info", ToastTypes.ERROR);
    } finally {
      setIsFetchingLogistics(false);
    }
  };

  const handleFetchLogisticsTrace = async (logisticsId = null) => {
    if (!order?.alibabaOrderIds?.length) {
      message.showToast("No Global order IDs to fetch trace", ToastTypes.WARNING);
      return;
    }
    
    // Check if logistics info has a logisticsId - handle nested array structure
    const hasLogisticsId = order?.logisticsInfo?.some(info => {
      // Check direct properties
      if (info?.logisticsId) return true;
      // Check result array structure (from logistics-info API)
      const resultArray = Array.isArray(info?.result) ? info.result : (info?.result ? [info.result] : []);
      return resultArray.some(r => r?.logisticsId || r?.logisticsBillNo);
    });
    
    if (!logisticsId && !hasLogisticsId) {
      message.showToast("No tracking number available yet. Seller may not have shipped the order.", ToastTypes.WARNING);
      return;
    }
    
    setIsFetchingTrace(true);
    try {
      const res = await crossBorderOrderService.fetchLogisticsTrace(id, logisticsId);
      if (res.status?.code === 200) {
        message.showToast("Logistics trace fetched successfully", ToastTypes.SUCCESS);
        setOrder(res.data.order);
      }
    } catch (error) {
      // Handle specific error messages
      const errorMsg = error?.response?.data?.status?.message || error?.message || "Failed to fetch logistics trace";
      if (errorMsg.includes("No logistics ID")) {
        message.showToast("No tracking number available yet. Seller may not have shipped the order.", ToastTypes.WARNING);
      } else {
        message.showToast(errorMsg, ToastTypes.ERROR);
      }
    } finally {
      setIsFetchingTrace(false);
    }
  };

  const formatPrice = (price, symbol = "¥") => {
    if (!price && price !== 0) return "-";
    return `${symbol} ${parseFloat(price).toFixed(2)}`;
  };

  // Handle navigation to order details page (same pattern as order management table)
  // First fetch the order by upsaleoOrderId to get its MongoDB _id, then navigate
  const handleOrderNavigate = async (upsaleoOrderId) => {
    if (!upsaleoOrderId) return;
    
    // Handle comma-separated order IDs by taking the first one
    const firstOrderId = upsaleoOrderId.split(", ")[0]?.trim();
    if (!firstOrderId) return;

    try {
      // Fetch the order by upsaleoOrderId to get its MongoDB _id
      const res = await orderApiService.getOrderById(firstOrderId);
      if (res.status?.code === 200 && res.data?._id) {
        // Navigate using the MongoDB _id (same as order management table)
        navigate(`/admin/order-management/details/${res.data._id}`);
      } else {
        message.showToast("Order not found", ToastTypes.ERROR);
      }
    } catch (error) {
      message.showToast(error?.message || "Failed to fetch order", ToastTypes.ERROR);
    }
  };

  const getProductDisplayName = (name) => {
    if (!name) return { original: "-", translated: null };
    const hasChinese = /[\u4e00-\u9fa5]/.test(name);
    if (hasChinese && translatedProducts[name]) {
      return { original: name, translated: translatedProducts[name] };
    }
    return { original: name, translated: null };
  };

  const getSupplierDisplayName = (name) => {
    if (!name) return "-";
    const translated = translatedNames[name];
    if (translated && translated !== name) {
      return { english: translated, chinese: name };
    }
    return { english: name, chinese: null };
  };

  // Group items by original Importerr order ID with their linked China order IDs
  const getImporterrOrdersMapping = () => {
    const orderMap = {};
    order?.items?.forEach(item => {
      const orderId = item.originalUpsaleoOrderId;
      if (!orderId) return;
      
      // Handle comma-separated order IDs (when multiple orders have same SKU)
      const orderIds = orderId.split(", ").map(id => id.trim()).filter(Boolean);
      
      orderIds.forEach(singleOrderId => {
        if (!orderMap[singleOrderId]) {
          orderMap[singleOrderId] = {
            orderId: singleOrderId,
            items: [],
            totalQty: 0,
            totalAmount: 0,
            linkedChinaOrderIds: new Set(),
          };
        }
        orderMap[singleOrderId].items.push(item);
        orderMap[singleOrderId].totalQty += item.quantity || 1;
        orderMap[singleOrderId].totalAmount += (item.price || 0) * (item.quantity || 1);
        
        // Add the linked China order ID for this item
        if (item.alibabaOrderId) {
          orderMap[singleOrderId].linkedChinaOrderIds.add(item.alibabaOrderId);
        }
      });
    });
    
    // Convert Set to Array
    return Object.values(orderMap).map(o => ({
      ...o,
      linkedChinaOrderIds: Array.from(o.linkedChinaOrderIds),
    }));
  };

  // Get unique China order IDs with their cross-border order reference
  const getChinaOrdersWithLinks = () => {
    const chinaOrders = [];
    order?.alibabaOrderIds?.forEach(alibabaId => {
      chinaOrders.push({
        alibabaOrderId: alibabaId,
        crossBorderOrderId: order._id,
        internalOrderId: order.internalOrderId,
      });
    });
    return chinaOrders;
  };

  // Order status steps with dates
  const getStatusSteps = () => {
    const orderCreatedDate = order?.createdAt ? moment(order.createdAt).format("YYYY-MM-DD HH:mm") : "";
    const processedDate = order?.processedAt ? moment(order.processedAt).format("YYYY-MM-DD HH:mm") : "";
    const payTime = order?.paymentInfo?.payTime || order?.buyerOrderDetail?.[0]?.result?.baseInfo?.payTime;
    const paymentDate = payTime ? moment(payTime, "YYYYMMDDHHmmssSSS").format("YYYY-MM-DD HH:mm") : "";
    
    // Check tracking history to determine if delivered and if has tracking info
    let isDeliveredFromTracking = false;
    let hasTrackingInfo = false;
    let deliveryDate = "";
    let latestTrackingDate = "";
    let latestTrackingEvent = null;
    
    if (order?.logisticsTrace?.length > 0) {
      for (const trace of order.logisticsTrace) {
        const traceArray = trace?.logisticsTrace || [];
        for (const traceItem of traceArray) {
          const steps = traceItem?.logisticsSteps || [];
          if (steps.length > 0) {
            hasTrackingInfo = true;
            // Get latest tracking event
            const lastStep = steps[steps.length - 1];
            if (!latestTrackingEvent || new Date(lastStep.acceptTime) > new Date(latestTrackingEvent.acceptTime)) {
              latestTrackingEvent = lastStep;
              latestTrackingDate = lastStep.acceptTime || lastStep.time || "";
            }
          }
          for (const step of steps) {
            const remark = (step.remark || step.desc || '').toLowerCase();
            if (remark.includes('delivered') || 
                remark.includes('已签收') || 
                remark.includes('has been delivered') ||
                remark.includes('签收') ||
                remark.includes('recipient')) {
              isDeliveredFromTracking = true;
              deliveryDate = step.acceptTime || step.time || "";
              break;
            }
          }
          if (isDeliveredFromTracking) break;
        }
        if (isDeliveredFromTracking) break;
      }
    }
    
    // Determine the label for step 4 based on tracking info
    const step4Label = hasTrackingInfo && !isDeliveredFromTracking ? "In Transit" : "Awaiting Buyer Confirm";
    const step4Date = hasTrackingInfo && !isDeliveredFromTracking ? latestTrackingDate : "";
    
    const steps = [
      { key: "pending", label: "Order Created", icon: FaFileAlt, date: orderCreatedDate },
      { key: "waitbuyerpay", label: "Waiting for Payment", icon: FaCreditCard, date: processedDate },
      { key: "waitsellersend", label: "Waiting for Seller to Ship", icon: FaBox, date: paymentDate },
      { key: "waitbuyerreceive", label: step4Label, icon: FaTruck, date: step4Date, latestTrackingEvent: hasTrackingInfo ? latestTrackingEvent : null },
      { key: "completed", label: "Completed", icon: FaCheckCircle, date: deliveryDate },
    ];
    
    const statusMap = {
      'pending': 0,
      'processing': 1,
      'payment_pending': 1,
      'waitbuyerpay': 1,
      'paid': 2,
      'waitsellersend': 2,
      'shipped': 3,
      'waitbuyerreceive': 3,
      'in_transit': 3,
      'delivered': 4,
      'completed': 4,
      'success': 4,
    };
    
    // Use alibabaStatus if available, otherwise fall back to internal status
    let effectiveStatus = order?.alibabaStatus || order?.status;
    
    // Override status if has tracking info but not delivered
    if (hasTrackingInfo && !isDeliveredFromTracking) {
      effectiveStatus = 'in_transit';
    }
    
    // Override status if tracking shows delivered
    if (isDeliveredFromTracking) {
      effectiveStatus = 'delivered';
    }
    
    const currentStep = statusMap[effectiveStatus?.toLowerCase()] ?? 0;
    return { steps, currentStep };
  };

  // Extract productItems from buyerOrderDetail for variant info
  const productItemsFromDetail = order?.buyerOrderDetail?.[0]?.result?.productItems || [];

  // Same total as admin order management (Importerr Price): mkp_total + shipping, or calculated from breakdown/orderDetails + shipping
  const getOrderTotalInr = (origOrder) => {
    if (!origOrder) return 0;
    const shippingCharges = Number(origOrder.totalShippingCharges) || 0;
    const mkpPrice = Number(origOrder.mkp_current_total_price) || 0;
    if (mkpPrice > 0) return mkpPrice + shippingCharges;
    const details = Array.isArray(origOrder.orderDetails) ? origOrder.orderDetails : [];
    const breakdownList = Array.isArray(origOrder.breakdown) ? origOrder.breakdown : [];
    const isManual = origOrder.marketplace?.toLowerCase() === 'manual' || origOrder.createdVia === 'manual_admin';
    const isWholesale = isManual && origOrder.orderType?.toLowerCase() === 'wholesale';
    const wholesaleFbpMap = new Map();
    breakdownList.forEach((b) => {
      if (b?.productId && b?.breakdown?.[0]) {
        const fbp = Number(b.breakdown[0].FBP ?? b.breakdown[0].fbp) || 0;
        if (fbp > 0) wholesaleFbpMap.set(String(b.productId), fbp);
      }
    });
    let total = 0;
    if (details.length > 0) {
      details.forEach((detail) => {
        const productId = detail?.product?._id?.toString() || detail?.product?.toString() || '';
        let price = 0;
        if (isWholesale && wholesaleFbpMap.has(productId)) price = wholesaleFbpMap.get(productId);
        else if (wholesaleFbpMap.has(productId)) price = wholesaleFbpMap.get(productId);
        else {
          const v = detail?.variant;
          price = Number(v?.finalPrice) || Number(v?.breakdown?.FBP ?? v?.breakdown?.fbp) || 0;
        }
        const qty = Math.max(1, Number(detail?.quantity) || 1);
        total += price * qty;
      });
    }
    if (total <= 0 && breakdownList.length > 0) {
      breakdownList.forEach((b) => {
        const fbp = Number(b?.breakdown?.[0]?.FBP ?? b?.breakdown?.[0]?.fbp) || 0;
        const qty = Number(b?.quantity) || 1;
        if (fbp > 0) total += fbp * qty;
      });
    }
    if (total > 0) return total + shippingCharges;
    const fallback = Number(origOrder.totalPrice) ?? Number(origOrder.totalConsumerPrice) ?? 0;
    return (Number.isFinite(fallback) ? fallback : 0) + shippingCharges;
  };

  // Importerr table data for ExcelTable
  const importerrTableData = useMemo(() => {
    if (!order?.items) return [];
    
    // Extract order reference from message
    const messageMatch = (order.message || '').match(/[：:]\s*([A-Z0-9]+)/i);
    const orderRef = messageMatch ? messageMatch[1] : (order.internalOrderId || 'CB');
    
    return order.items.map((item, idx) => {
      const skuLetter = String.fromCharCode(65 + idx);
      const itemQty = item.quantity || 1;
      const productChinaNo = `${orderRef}-${skuLetter}-${itemQty}`;
      const originalOrderId = item.originalUpsaleoOrderId?.split(', ')[0]?.trim();
      const originalOrder = originalOrders.find(o => String(o.upsaleoOrderId) === String(originalOrderId) || String(o.orderId) === String(originalOrderId));
      // Same total as admin order management: computed order total (INR); else stored itemTotalInr; else 0
      const orderTotalInr = originalOrder ? getOrderTotalInr(originalOrder) : 0;
      const itemTotal = orderTotalInr > 0
        ? orderTotalInr
        : (item.itemTotalInr != null && Number(item.itemTotalInr) > 0)
          ? Number(item.itemTotalInr)
          : 0;
      
      // Get product name with translation
      const productName = getProductDisplayName(item.productName);
      
      // Get variant name from buyerOrderDetail
      const matchingProductItem = productItemsFromDetail.find(p => p.specId === item.specId || p.productID === item.offerId);
      const variantNameCn = matchingProductItem?.skuInfos?.map(s => `${s.name}: ${s.value}`).join(', ') || '-';
      
      const hsnInfo = originalOrder?.hsnInfo || {};
      
      return {
        _id: item._id || idx,
        productChinaNo,
        imageUrl: item.imageUrl || matchingProductItem?.productImgUrl?.[0],
        productName: productName.original,
        productNameEn: productName.translated,
        variantSku: item.originalSku || item.specId || '-',
        variantNameCn,
        variantNameEn: translatedProducts[variantNameCn] || null,
        itemQty,
        itemTotal,
        originalOrderId: item.originalUpsaleoOrderId || '-',
        importCategory: hsnInfo.category || hsnInfo.Item || '-',
        hsn: hsnInfo.hsnCode || hsnInfo.HSN || '-',
        taxRate: hsnInfo.IGST || hsnInfo.gstRate || null,
        customDuty: hsnInfo.FinalCustomDuty || hsnInfo.BCD || null,
        allottedMakId: item.allottedMakId || '-',
      };
    });
  }, [order, originalOrders, translatedProducts, productItemsFromDetail]);

  const importerrItemsTotalInr = useMemo(
    () => importerrTableData.reduce((s, row) => s + (Number(row.itemTotal) || 0), 0),
    [importerrTableData]
  );

  // Importerr table columns for ExcelTable
  const importerrColumns = useMemo(() => [
    {
      key: 'productChinaNo',
      label: 'Product China No',
      width: 120,
      render: (v) => <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#4361ee' }}>{v}</span>
    },
    {
      key: 'imageUrl',
      label: 'Product Image',
      width: 80,
      render: (v) => <img src={v} alt="" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4, border: '1px solid #e5e7eb' }} />
    },
    {
      key: 'productName',
      label: 'Product Name',
      width: 280,
      wrap: true,
      render: (v, row) => (
        <div style={{ lineHeight: 1.4 }}>
          {row.productNameEn && <div style={{ fontSize: 12, color: '#1f2937', fontWeight: 500, marginBottom: 4 }}>{row.productNameEn}</div>}
          <div style={{ fontSize: 11, color: row.productNameEn ? '#9ca3af' : '#1f2937' }}>{v}</div>
        </div>
      )
    },
    {
      key: 'variantSku',
      label: 'Variant SKU',
      width: 120,
      render: (v) => <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#4b5563' }}>{v}</span>
    },
    {
      key: 'variantNameCn',
      label: 'Variant Name',
      width: 150,
      wrap: true,
      render: (v, row) => (
        <div style={{ lineHeight: 1.4 }}>
          {row.variantNameEn && <div style={{ fontSize: 11, color: '#1f2937', fontWeight: 500, marginBottom: 2 }}>{row.variantNameEn}</div>}
          <div style={{ fontSize: 10, color: row.variantNameEn ? '#9ca3af' : '#6b7280' }}>{v}</div>
        </div>
      )
    },
    {
      key: 'itemQty',
      label: 'Item Quantity',
      width: 100,
      render: (v) => <span style={{ fontWeight: 500 }}>{v}</span>
    },
    {
      key: 'itemTotal',
      label: 'Item Total',
      width: 100,
      render: (v) => <span style={{ color: '#ff6600', fontWeight: 500 }}>{formatPrice(v, '₹')}</span>
    },
    {
      key: 'originalOrderId',
      label: 'Original Order ID',
      width: 150,
      render: (v) => (
        <span 
          style={{ fontFamily: 'monospace', fontSize: 11, color: '#4361ee', cursor: 'pointer' }}
          onClick={(e) => { e.stopPropagation(); handleOrderNavigate(v); }}
        >
          {v}
        </span>
      )
    },
    {
      key: 'importCategory',
      label: 'Import Category',
      width: 150,
      render: (v) => <span style={{ fontSize: 11, color: '#6b7280' }}>{v}</span>
    },
    {
      key: 'hsn',
      label: 'HSN',
      width: 100,
      render: (v) => <span style={{ fontSize: 11, color: '#6b7280' }}>{v}</span>
    },
    {
      key: 'taxRate',
      label: 'Tax Rate',
      width: 80,
      render: (v) => <span style={{ fontSize: 11, color: '#6b7280' }}>{v ? `${v}%` : '-'}</span>
    },
    {
      key: 'customDuty',
      label: 'Custom Duty',
      width: 100,
      render: (v) => <span style={{ fontSize: 11, color: '#6b7280' }}>{v ? `${v}%` : '-'}</span>
    },
    {
      key: 'allottedMakId',
      label: 'Allotted Mak ID',
      width: 120,
      render: (v) => <span style={{ fontSize: 11, color: '#6b7280' }}>{v}</span>
    },
  ], [handleOrderNavigate]);

  if (!order) {
    return (
      <div className="cb-detail-page">
        <div className="cb-detail-loading">Loading...</div>
      </div>
    );
  }

  // Extract 1688 order detail
  const orderDetail = order.buyerOrderDetail?.[0];
  const buyerDetail = orderDetail?.result || {};
  const baseInfo = buyerDetail?.baseInfo || {};
  const productItems = buyerDetail?.productItems || [];
  const nativeLogistics = buyerDetail?.nativeLogistics || {};
  const sellerContact = baseInfo?.sellerContact || {};

  const sourceRmb = order.sourceSumProductPayment != null && Number(order.sourceSumProductPayment) > 0 ? Number(order.sourceSumProductPayment) : null;
  const totalQty = productItems.length ? productItems.reduce((s, i) => s + (i.quantity || 0), 0) : (order.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0);
  const effectiveItemsTotalRmb = sourceRmb ?? baseInfo.sumProductPayment ?? order.paymentInfo?.sumProductPayment ?? order.totalAmount ?? 0;
  const effectiveUnitPriceRmb = (sourceRmb != null && totalQty > 0) ? (sourceRmb / totalQty) : null;
  const shippingFeeRmb = baseInfo.shippingFee ?? order.paymentInfo?.shippingFee ?? 0;
  const effectiveToPayRmb = sourceRmb != null ? (effectiveItemsTotalRmb + shippingFeeRmb) : (baseInfo.totalAmount || order.paymentInfo?.totalAmount || order.totalAmount);

  const getStoredUnitPriceRmb = (pi) => {
    if (!order.items?.length) return null;
    const bySpec = order.items.find(oi => oi.specId === pi.specId);
    if (bySpec?.price != null) return bySpec.price;
    const byOffer = order.items.find(oi => oi.offerId === pi.productID);
    return byOffer?.price ?? null;
  };

  const { steps, currentStep } = getStatusSteps();
  const supplierName = getSupplierDisplayName(order.supplierName || sellerContact.companyName);

  return (
    <div className="cb-detail-page">
      {/* Top Actions - Back & Refresh */}
      <div className="cb-detail-top-actions">
        <button className="back-btn" onClick={() => navigate("/admin/cross-border-orders")}>
          <FaArrowLeft /> Back to Orders
        </button>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="refresh-btn" onClick={handleRefreshDetails} disabled={isRefreshing}>
            <FaSync className={isRefreshing ? 'spin' : ''} />
            {isRefreshing ? "Refreshing..." : "Refresh from Global"}
          </button>
          <button 
            className="refresh-btn" 
            onClick={handleFetchLogisticsInfo} 
            disabled={isFetchingLogistics}
            style={{ background: '#10b981', borderColor: '#10b981' }}
          >
            <FaTruck />
            {isFetchingLogistics ? "Fetching..." : "Fetch Logistics"}
          </button>
        </div>
      </div>

      {/* Order Status Timeline */}
      <div className="cb-status-timeline">
        <div className="timeline-steps">
          {steps.map((step, idx) => {
            const IconComponent = step.icon;
            // Build tooltip for steps with tracking info
            let tooltipText = '';
            if (step.latestTrackingEvent) {
              const time = step.latestTrackingEvent.acceptTime || '';
              const remark = step.latestTrackingEvent.remark || step.latestTrackingEvent.desc || '';
              const shortRemark = remark.length > 80 ? remark.substring(0, 80) + '...' : remark;
              tooltipText = `${time}\n${shortRemark}`;
            }
            return (
              <div 
                key={step.key} 
                className={`timeline-step ${idx <= currentStep ? 'active' : ''} ${idx === currentStep ? 'current' : ''}`}
                title={tooltipText}
                style={{ cursor: step.latestTrackingEvent ? 'help' : 'default' }}
              >
                <div className="step-icon">
                  <IconComponent />
                </div>
                <div className="step-label">{step.label}</div>
                <div className="step-date">{idx <= currentStep && step.date ? step.date : ""}</div>
              </div>
            );
          })}
          <div className="timeline-line">
            <div className="timeline-progress" style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}></div>
          </div>
        </div>
      </div>

      {/* Order Status Alert */}
      {order.status === 'failed' && (
        <div className="cb-status-alert error">
          <i className="fas fa-exclamation-circle"></i>
          <span>Order Failed: {order.apiError || "Unknown error"}</span>
        </div>
      )}

      {baseInfo.status === 'waitbuyerpay' && (
        <div className="cb-status-alert warning">
          <i className="fas fa-clock"></i>
          <span>Order Status: <strong>Waiting for buyer to make the payment.</strong></span>
          <span className="alert-hint">Please complete payment on 1688.com</span>
        </div>
      )}

      {/* Tabs */}
      <div className="cb-detail-tabs">
        <button className={activeTab === "order" ? "active" : ""} onClick={() => setActiveTab("order")}>
          Order Information
        </button>
        <button className={activeTab === "importerr" ? "active" : ""} onClick={() => setActiveTab("importerr")}>
          Importerr Orders
        </button>
        <button className={activeTab === "logistics" ? "active" : ""} onClick={() => setActiveTab("logistics")}>
          Logistics Information
        </button>
        <button className={activeTab === "transaction" ? "active" : ""} onClick={() => setActiveTab("transaction")}>
          Method of Transaction
        </button>
      </div>

      {/* Tab Content */}
      <div className="cb-detail-content">
        {activeTab === "order" && (
          <div className="cb-order-info-section">
            {/* Two Column Layout */}
            <div className="cb-info-grid">
              {/* Shipping Information */}
              <div className="cb-info-block">
                <h4>Shipping Information</h4>
                <div className="cb-detail-info-row">
                  <span className="cb-detail-info-label">Order Number:</span>
                  <span className="cb-detail-info-value highlight">{order.alibabaOrderIds?.[0] || order.internalOrderId}</span>
                </div>
                <div className="cb-detail-info-row">
                  <span className="cb-detail-info-label">Recipient:</span>
                  <span className="cb-detail-info-value">{nativeLogistics.contactPerson || order.addressParam?.fullName}</span>
                </div>
                <div className="cb-detail-info-row">
                  <span className="cb-detail-info-label">Delivery Address:</span>
                  <span className="cb-detail-info-value">
                    {nativeLogistics.province || order.addressParam?.provinceText} {nativeLogistics.city || order.addressParam?.cityText} {nativeLogistics.area || order.addressParam?.areaText} {nativeLogistics.address || order.addressParam?.address}
                  </span>
                </div>
                <div className="cb-detail-info-row">
                  <span className="cb-detail-info-label">Mobile Phone:</span>
                  <span className="cb-detail-info-value">{nativeLogistics.mobile || order.addressParam?.mobile}</span>
                </div>
                <div className="cb-detail-info-row">
                  <span className="cb-detail-info-label">Telephone:</span>
                  <span className="cb-detail-info-value">{nativeLogistics.telephone || order.addressParam?.phone || "-"}</span>
                </div>
                <div className="cb-detail-info-row">
                  <span className="cb-detail-info-label">Delivery Time:</span>
                  <span className="cb-detail-info-value">After payment 按双方约定</span>
                </div>
              </div>

              {/* Seller Information */}
              <div className="cb-info-block">
                <h4>Seller Information</h4>
                <div className="cb-detail-info-row">
                  <span className="cb-detail-info-label">Supplier:</span>
                  <span className="cb-detail-info-value">
                    {supplierName.english}
                    {supplierName.chinese && (
                      <span className="chinese-text"> ({supplierName.chinese})</span>
                    )}
                    <FaStore className="seller-icon" />
                  </span>
                </div>
                <div className="cb-detail-info-row">
                  <span className="cb-detail-info-label">Shop Name:</span>
                  <span className="cb-detail-info-value">{sellerContact.shopName || "-"}</span>
                </div>
                <div className="cb-detail-info-row">
                  <span className="cb-detail-info-label">Mobile Phone:</span>
                  <span className="cb-detail-info-value">{sellerContact.phone || "Please contact via 1688"}</span>
                </div>
                <div className="cb-detail-info-row">
                  <span className="cb-detail-info-label">Seller ID:</span>
                  <span className="cb-detail-info-value">{order.supplierId || baseInfo.sellerID || "-"}</span>
                </div>
              </div>
            </div>

            {/* Product Items Table - Excel Style */}
            <div className="cb-products-section">
              <div className="cb-excel-table-wrapper">
                <table className="cb-excel-table">
                  <thead>
                    <tr>
                      <th className="col-goods">GOODS</th>
                      <th className="col-price">UNIT PRICE (¥)</th>
                      <th className="col-qty">QUANTITY</th>
                      <th className="col-discount">DISCOUNT (¥)</th>
                      <th className="col-status">GOODS STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productItems.length > 0 ? (
                      productItems.map((item, idx) => {
                        const productName = getProductDisplayName(item.name);
                        const statusText = item.statusStr || item.status || "waitbuyerpay";
                        
                        // Known status mappings
                        const statusMap = {
                          'waitbuyerpay': { cn: '等待买家付款', en: 'Waiting for Payment' },
                          'waitsellersend': { cn: '等待卖家发货', en: 'Waiting for Shipment' },
                          'waitbuyerreceive': { cn: '等待买家收货', en: 'Waiting for Receipt' },
                          'success': { cn: '交易成功', en: 'Completed' },
                          'completed': { cn: '交易成功', en: 'Completed' },
                          'cancel': { cn: '交易取消', en: 'Cancelled' },
                          'pending': { cn: '等待买家付款', en: 'Waiting for Payment' },
                        };
                        
                        let statusInfo;
                        const lowerStatus = statusText.toLowerCase();
                        
                        if (statusMap[lowerStatus]) {
                          statusInfo = statusMap[lowerStatus];
                        } else if (/[\u4e00-\u9fa5]/.test(statusText)) {
                          // Chinese text - use translation if available
                          statusInfo = {
                            cn: statusText,
                            en: translatedStatuses[statusText] || statusText
                          };
                        } else {
                          statusInfo = { cn: statusText, en: statusText };
                        }
                        
                        return (
                          <tr key={idx}>
                            <td className="col-goods">
                              <div className="goods-cell">
                                <img src={item.productImgUrl?.[0] || item.imageUrl} alt="" className="goods-img" />
                                <div className="goods-info">
                                  <div className="goods-name">
                                    {productName.original}
                                    {productName.translated && (
                                      <span className="goods-name-en">{productName.translated}</span>
                                    )}
                                  </div>
                                  <div className="goods-sku">
                                    {item.skuInfos?.map((sku, i) => (
                                      <span key={i} className="sku-tag">{sku.name}: {sku.value}</span>
                                    ))}
                                  </div>
                                  <div className="goods-codes">
                                    <span>Offer ID: {item.productID}</span>
                                    <span>Spec ID: {item.specId}</span>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="col-price">{formatPrice(effectiveUnitPriceRmb ?? getStoredUnitPriceRmb(item) ?? item.price)}</td>
                            <td className="col-qty">{item.quantity}</td>
                            <td className="col-discount">-</td>
                            <td className="col-status">
                              <div className={`status-tag ${lowerStatus}`}>
                                <span className="status-cn">{statusInfo.cn}</span>
                                <span className="status-en">{statusInfo.en}</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      order.items?.map((item, idx) => {
                        const productName = getProductDisplayName(item.productName);
                        return (
                          <tr key={idx}>
                            <td className="col-goods">
                              <div className="goods-cell">
                                <img src={item.imageUrl} alt="" className="goods-img" />
                                <div className="goods-info">
                                  <div className="goods-name">
                                    {productName.original}
                                    {productName.translated && (
                                      <span className="goods-name-en">{productName.translated}</span>
                                    )}
                                  </div>
                                  <div className="goods-codes">
                                    <span>Offer ID: {item.offerId}</span>
                                    <span>Spec ID: {item.specId}</span>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="col-price">{formatPrice(effectiveUnitPriceRmb ?? item.price)}</td>
                            <td className="col-qty">{item.quantity}</td>
                            <td className="col-discount">-</td>
                            <td className="col-status">
                              <div className="status-tag pending">
                                <span className="status-cn">等待买家付款</span>
                                <span className="status-en">Waiting for Payment</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Shipping Note */}
              <div className="cb-shipping-note">
                <FaTruck />
                <span>Free Return Shipping: Pickup at the door, up to 100% of the return shipping costs can be reimbursed.</span>
              </div>
            </div>

            {/* Order Summary */}
            <div className="cb-order-summary">
              <div className="summary-row">
                <span>Items Total:</span>
                <span className="summary-value">{formatPrice(effectiveItemsTotalRmb)}</span>
              </div>
              <div className="summary-row">
                <span>Shipping Fee:</span>
                <span className="summary-value">{formatPrice(shippingFeeRmb)}</span>
              </div>
              <div className="summary-row total">
                <span>To Pay:</span>
                <span className="summary-total">{formatPrice(effectiveToPayRmb)} (≈ ${(Number(effectiveToPayRmb) / 7.2).toFixed(2)})</span>
              </div>
            </div>

            {/* Buyer's Message */}
            <div className="cb-buyer-message">
              <h4>Buyer's Message</h4>
              <p>{order.message || baseInfo.remark || "-"}</p>
            </div>
          </div>
        )}

        {activeTab === "logistics" && (
          <div className="cb-logistics-section">
            {/* Fetch Buttons */}
            <div className="cb-logistics-actions" style={{ marginBottom: '20px', display: 'flex', gap: '12px' }}>
              <button 
                className="refresh-btn" 
                onClick={handleFetchLogisticsInfo} 
                disabled={isFetchingLogistics}
                style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #4361ee', background: '#4361ee', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <FaSync className={isFetchingLogistics ? 'spin' : ''} />
                {isFetchingLogistics ? "Fetching..." : "Fetch Logistics Info"}
              </button>
              {(() => {
                // Check for logisticsId in the new array structure or old structure
                const hasLogisticsId = order?.logisticsInfo?.some(info => {
                  const resultArray = Array.isArray(info?.result) ? info.result : [info?.result];
                  return resultArray.some(r => r?.logisticsId || r?.logisticsBillNo);
                });
                const canFetchTrace = hasLogisticsId && !isFetchingTrace;
                return (
                  <button 
                    className="refresh-btn" 
                    onClick={() => handleFetchLogisticsTrace()} 
                    disabled={!canFetchTrace}
                    title={!hasLogisticsId ? "No tracking number available yet. Seller may not have shipped." : "Fetch tracking history"}
                    style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #10b981', background: canFetchTrace ? '#10b981' : '#9ca3af', color: '#fff', cursor: canFetchTrace ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <FaTruck />
                    {isFetchingTrace ? "Fetching..." : "Fetch Tracking Info"}
                  </button>
                );
              })()}
            </div>

            {/* Two-column grid for Logistics Info and Tracking History - 50% each */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              {/* Logistics Info from API */}
              {order?.logisticsInfo?.length > 0 && (
                <div className="cb-info-block" style={{ marginBottom: '0' }}>
                  <h4>Logistics Information (from Global API)</h4>
                  {order.logisticsInfo.map((info, idx) => {
                    const hasError = info?.error || info?.success === false;
                    const errorMessage = info?.error || "Unknown error";
                    
                    // Handle both array and object result structures
                    const resultArray = Array.isArray(info?.result) ? info.result : (info?.result ? [info.result] : []);
                    
                    return (
                      <div key={idx} style={{ marginBottom: '16px', padding: '12px', background: hasError ? '#fef2f2' : '#f9fafb', borderRadius: '8px', border: hasError ? '1px solid #fecaca' : '1px solid #e5e7eb' }}>
                        <div className="cb-detail-info-row">
                          <span className="cb-detail-info-label">Order ID:</span>
                          <span className="cb-detail-info-value" style={{ fontFamily: 'monospace' }}>{info.orderId}</span>
                        </div>
                        
                        {/* Status indicator */}
                        <div className="cb-detail-info-row">
                          <span className="cb-detail-info-label">Status:</span>
                          <span className="cb-detail-info-value">
                            {hasError ? (
                              <span style={{ color: '#dc2626', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <i className="fas fa-exclamation-circle"></i> Error
                              </span>
                          ) : (
                            <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <i className="fas fa-check-circle"></i> Success
                            </span>
                          )}
                        </span>
                      </div>
                      
                      {/* Error message if any */}
                      {hasError && (
                        <div className="cb-detail-info-row" style={{ background: '#fee2e2', padding: '8px', borderRadius: '4px', marginTop: '8px' }}>
                          <span className="cb-detail-info-label" style={{ color: '#dc2626' }}>Error:</span>
                          <span className="cb-detail-info-value" style={{ color: '#dc2626' }}>{errorMessage}</span>
                        </div>
                      )}
                      
                      {/* Logistics details - iterate through result array */}
                      {!hasError && resultArray.length > 0 && resultArray.map((logisticsItem, lIdx) => (
                        <div key={lIdx} style={{ marginTop: '12px', padding: '12px', background: '#fff', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                          {/* Logistics Company */}
                          {(logisticsItem?.logisticsCompanyName || logisticsItem?.company) && (
                            <div className="cb-detail-info-row">
                              <span className="cb-detail-info-label">Logistics Company:</span>
                              <span className="cb-detail-info-value" style={{ fontWeight: 500 }}>
                                {logisticsItem.logisticsCompanyName || logisticsItem.company}
                                {logisticsItem.logisticsCompanyNo && <span style={{ color: '#6b7280', marginLeft: '8px' }}>({logisticsItem.logisticsCompanyNo})</span>}
                              </span>
                            </div>
                          )}
                          
                          {/* Tracking Number */}
                          {(logisticsItem?.logisticsBillNo || logisticsItem?.name) && (
                            <div className="cb-detail-info-row">
                              <span className="cb-detail-info-label">Tracking Number:</span>
                              <span className="cb-detail-info-value highlight" style={{ fontFamily: 'monospace', fontWeight: 600, color: '#4361ee', fontSize: '14px' }}>
                                {logisticsItem.logisticsBillNo || logisticsItem.name}
                              </span>
                            </div>
                          )}
                          
                          {/* Logistics ID */}
                          {logisticsItem?.logisticsId && (
                            <div className="cb-detail-info-row">
                              <span className="cb-detail-info-label">Logistics ID:</span>
                              <span className="cb-detail-info-value" style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' }}>{logisticsItem.logisticsId}</span>
                            </div>
                          )}
                          
                          {/* Logistics Status - Derive actual status from tracking history if available */}
                          {logisticsItem?.status && (
                            <div className="cb-detail-info-row">
                              <span className="cb-detail-info-label">Logistics Status:</span>
                              <span className="cb-detail-info-value">
                                {(() => {
                                  // Check tracking history to determine actual delivery status
                                  let derivedStatus = logisticsItem.status;
                                  
                                  // Look for tracking events that indicate delivery
                                  const matchingTrace = order?.logisticsTrace?.find(t => 
                                    t.logisticsId === logisticsItem.logisticsId || 
                                    t.logisticsTrace?.some(lt => lt.logisticsId === logisticsItem.logisticsId || lt.logisticsBillNo === logisticsItem.logisticsBillNo)
                                  );
                                  
                                  if (matchingTrace) {
                                    const traceArray = matchingTrace?.logisticsTrace || [];
                                    for (const traceItem of traceArray) {
                                      const steps = traceItem?.logisticsSteps || [];
                                      // Check if any step indicates delivery (delivered, signed, 已签收, etc.)
                                      const isDelivered = steps.some(step => {
                                        const remark = (step.remark || step.desc || '').toLowerCase();
                                        return remark.includes('delivered') || 
                                               remark.includes('已签收') || 
                                               remark.includes('has been delivered') ||
                                               remark.includes('签收') ||
                                               remark.includes('recipient');
                                      });
                                      if (isDelivered) {
                                        derivedStatus = 'SIGNED';
                                        break;
                                      }
                                    }
                                  }
                                  
                                  // Map logistics status codes to readable labels
                                  // If has tracking info but not delivered, show "In Transit"
                                  const hasTrackingSteps = matchingTrace?.logisticsTrace?.some(lt => lt?.logisticsSteps?.length > 0);
                                  
                                  // If we have tracking steps but not delivered, override to "In Transit"
                                  if (hasTrackingSteps && derivedStatus !== 'SIGNED') {
                                    derivedStatus = 'TRANSIT';
                                  }
                                  
                                  const statusMap = {
                                    'SIGN': { label: 'In Transit', bg: '#fef3c7', color: '#b45309' },
                                    'TRANSIT': { label: 'In Transit', bg: '#fef3c7', color: '#b45309' },
                                    'DELIVERING': { label: 'Out for Delivery', bg: '#fef3c7', color: '#92400e' },
                                    'SIGNED': { label: 'Delivered', bg: '#dcfce7', color: '#166534' },
                                    'PROBLEM': { label: 'Problem', bg: '#fee2e2', color: '#dc2626' },
                                    'RETURNED': { label: 'Returned', bg: '#fee2e2', color: '#dc2626' },
                                  };
                                  const statusInfo = statusMap[derivedStatus] || { label: derivedStatus, bg: '#f3f4f6', color: '#374151' };
                                  
                                  // Get latest tracking event for tooltip
                                  let latestEvent = null;
                                  if (matchingTrace) {
                                    const traceArray = matchingTrace?.logisticsTrace || [];
                                    for (const traceItem of traceArray) {
                                      const steps = traceItem?.logisticsSteps || [];
                                      if (steps.length > 0) {
                                        const lastStep = steps[steps.length - 1];
                                        if (!latestEvent || new Date(lastStep.acceptTime) > new Date(latestEvent.acceptTime)) {
                                          latestEvent = lastStep;
                                        }
                                      }
                                    }
                                  }
                                  
                                  const tooltipText = latestEvent 
                                    ? `${latestEvent.acceptTime || ''}\n${(latestEvent.remark || latestEvent.desc || '').substring(0, 100)}${(latestEvent.remark || '').length > 100 ? '...' : ''}`
                                    : '';
                                  
                                  return (
                                    <span 
                                      style={{ 
                                        padding: '2px 8px', 
                                        borderRadius: '4px', 
                                        fontSize: '12px',
                                        background: statusInfo.bg,
                                        color: statusInfo.color,
                                        cursor: latestEvent ? 'help' : 'default'
                                      }}
                                      title={tooltipText}
                                    >
                                      {statusInfo.label}
                                    </span>
                                  );
                                })()}
                              </span>
                            </div>
                          )}
                          
                          {/* Sender Info */}
                          {logisticsItem?.sender && (
                            <div className="cb-detail-info-row" style={{ marginTop: '8px' }}>
                              <span className="cb-detail-info-label">Sender:</span>
                              <span className="cb-detail-info-value" style={{ fontSize: '13px' }}>
                                {logisticsItem.sender.senderName && <span style={{ fontWeight: 500 }}>{logisticsItem.sender.senderName}</span>}
                                {logisticsItem.sender.senderMobile && <span style={{ marginLeft: '8px', color: '#6b7280' }}>{logisticsItem.sender.senderMobile}</span>}
                                {(logisticsItem.sender.senderProvince || logisticsItem.sender.senderCity) && (
                                  <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '2px' }}>
                                    {[logisticsItem.sender.senderProvince, logisticsItem.sender.senderCity, logisticsItem.sender.senderAddress].filter(Boolean).join(' ')}
                                  </div>
                                )}
                              </span>
                            </div>
                          )}
                          
                          {/* Receiver Info */}
                          {logisticsItem?.receiver && (
                            <div className="cb-detail-info-row" style={{ marginTop: '8px' }}>
                              <span className="cb-detail-info-label">Receiver:</span>
                              <span className="cb-detail-info-value" style={{ fontSize: '13px' }}>
                                {logisticsItem.receiver.receiverName && <span style={{ fontWeight: 500 }}>{logisticsItem.receiver.receiverName}</span>}
                                {logisticsItem.receiver.receiverMobile && <span style={{ marginLeft: '8px', color: '#6b7280' }}>{logisticsItem.receiver.receiverMobile}</span>}
                                {(logisticsItem.receiver.receiverProvince || logisticsItem.receiver.receiverCity) && (
                                  <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '2px' }}>
                                    {[logisticsItem.receiver.receiverProvince, logisticsItem.receiver.receiverCity, logisticsItem.receiver.receiverCounty, logisticsItem.receiver.receiverAddress].filter(Boolean).join(' ')}
                                  </div>
                                )}
                              </span>
                            </div>
                          )}
                          
                          {/* Goods Info */}
                          {logisticsItem?.sendGoods?.length > 0 && (
                            <div className="cb-detail-info-row" style={{ marginTop: '8px' }}>
                              <span className="cb-detail-info-label">Goods:</span>
                              <span className="cb-detail-info-value" style={{ fontSize: '12px' }}>
                                {logisticsItem.sendGoods.map((good, gIdx) => (
                                  <div key={gIdx} style={{ marginBottom: '4px' }}>
                                    {good.goodName} × {good.quantity} {good.unit}
                                  </div>
                                ))}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {/* Show if no logistics data yet */}
                      {!hasError && resultArray.length === 0 && (
                        <div style={{ color: '#f59e0b', fontSize: '13px', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <i className="fas fa-info-circle"></i>
                          No logistics data available yet. Seller may not have shipped the order.
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              )}

              {/* Logistics Trace / Tracking - Adjacent to Logistics Info */}
              {order?.logisticsTrace?.length > 0 && (
                <div className="cb-info-block" style={{ marginBottom: '0', maxHeight: '600px', overflowY: 'auto' }}>
                  <h4>Tracking History</h4>
                  {order.logisticsTrace.map((trace, idx) => {
                    const hasError = trace?.success === false;
                    const errorMessage = trace?.errorMessage || trace?.error;
                    
                    // Extract tracking events from the nested structure
                    // API returns: { logisticsTrace: [{ logisticsId, orderId, logisticsBillNo, logisticsSteps: [...] }] }
                    const logisticsTraceArray = trace?.logisticsTrace || [];
                    const allTrackingEvents = [];
                    
                    // Iterate through each logistics trace item and extract logisticsSteps
                    logisticsTraceArray.forEach((traceItem) => {
                    const steps = traceItem?.logisticsSteps || [];
                    steps.forEach((step) => {
                      allTrackingEvents.push({
                        ...step,
                        logisticsBillNo: traceItem.logisticsBillNo,
                        logisticsId: traceItem.logisticsId,
                      });
                    });
                  });
                  
                  // Sort events by time (most recent first)
                  allTrackingEvents.sort((a, b) => {
                    const timeA = a.acceptTime || a.time || '';
                    const timeB = b.acceptTime || b.time || '';
                    return timeB.localeCompare(timeA);
                  });
                  
                  // Get tracking number from first trace item
                  const trackingNumber = logisticsTraceArray[0]?.logisticsBillNo || trace.logisticsId;
                  
                  return (
                    <div key={idx} style={{ marginBottom: '16px' }}>
                      <div style={{ marginBottom: '8px', fontWeight: 500, color: '#4361ee' }}>
                        Logistics ID: {trace.logisticsId} | Tracking #: {trackingNumber} | Order: {trace.orderId}
                      </div>
                      
                      {/* Show error message if present */}
                      {errorMessage && (
                        <div style={{ 
                          padding: '12px', 
                          background: '#fef3c7', 
                          border: '1px solid #fcd34d', 
                          borderRadius: '6px', 
                          marginBottom: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <i className="fas fa-info-circle" style={{ color: '#d97706' }}></i>
                          <span style={{ color: '#92400e', fontSize: '13px' }}>{errorMessage}</span>
                        </div>
                      )}
                      
                      {allTrackingEvents.length > 0 ? (
                        <div className="tracking-timeline" style={{ borderLeft: '2px solid #e5e7eb', paddingLeft: '16px', marginLeft: '8px' }}>
                          {allTrackingEvents.map((event, eventIdx) => (
                            <div key={eventIdx} style={{ marginBottom: '12px', position: 'relative' }}>
                              <div style={{ position: 'absolute', left: '-22px', top: '4px', width: '12px', height: '12px', borderRadius: '50%', background: eventIdx === 0 ? '#10b981' : '#d1d5db' }}></div>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>{event.acceptTime || event.time}</div>
                              <div style={{ fontSize: '13px', color: '#1f2937' }}>{event.remark || event.desc || event.acceptAddress}</div>
                            </div>
                          ))}
                        </div>
                      ) : !errorMessage && (
                        <div style={{ color: '#9ca3af', fontSize: '13px' }}>No tracking events available yet. The courier may not have scanned the package.</div>
                      )}
                    </div>
                  );
                })}
                </div>
              )}
            </div>

            {/* Original Logistics from Order Detail */}
          </div>
        )}

        {activeTab === "importerr" && (
          <div className="cb-importerr-section">
            <ExcelTable
              data={importerrTableData}
              columns={importerrColumns}
              title={`Product Items (${order.items?.length || 0})`}
              sortable
              resizable
              showToolbar
              showToolbarExport
              showToolbarColumns
              showToolbarFullscreen
            />

            {/* Order Summary */}
            <div className="cb-order-summary" style={{marginTop: '20px'}}>
              <div className="summary-row">
                <span>Total Items:</span>
                <span className="summary-value">{order.items?.length || 0} SKUs</span>
              </div>
              <div className="summary-row">
                <span>Total Quantity:</span>
                <span className="summary-value">{order.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0}</span>
              </div>
              <div className="summary-row">
                <span>Items Total:</span>
                <span className="summary-value">{formatPrice(importerrItemsTotalInr || 0, '₹')}</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === "transaction" && (
          <div className="cb-transaction-section">
            <div className="cb-info-block">
              <h4>Transaction Details</h4>
              <div className="cb-detail-info-row">
                <span className="cb-detail-info-label">Trade Type:</span>
                <span className="cb-detail-info-value">{baseInfo.tradeTypeDesc || order.paymentInfo?.tradeTypeDesc || "Cross-border Trade"}</span>
              </div>
              <div className="cb-detail-info-row">
                <span className="cb-detail-info-label">Payment Status:</span>
                <span className="cb-detail-info-value">
                  <span className={`payment-status ${(baseInfo.status || order.alibabaStatus || order.status)?.toLowerCase()}`}>
                    {baseInfo.status || order.alibabaStatus || order.status}
                  </span>
                </span>
              </div>
              <div className="cb-detail-info-row">
                <span className="cb-detail-info-label">Create Time:</span>
                <span className="cb-detail-info-value">{baseInfo.createTime || moment(order.createdAt).format("YYYY-MM-DD HH:mm:ss")}</span>
              </div>
              <div className="cb-detail-info-row">
                <span className="cb-detail-info-label">Last Modified:</span>
                <span className="cb-detail-info-value">{baseInfo.modifyTime || moment(order.updatedAt).format("YYYY-MM-DD HH:mm:ss")}</span>
              </div>
              {(baseInfo.payTime || order.paymentInfo?.payTime) && (
                <div className="cb-detail-info-row">
                  <span className="cb-detail-info-label">Payment Time:</span>
                  <span className="cb-detail-info-value">{baseInfo.payTime || order.paymentInfo?.payTime}</span>
                </div>
              )}
              {(baseInfo.alipayTradeId || order.paymentInfo?.alipayTradeId) && (
                <div className="cb-detail-info-row">
                  <span className="cb-detail-info-label">Alipay Trade ID:</span>
                  <span className="cb-detail-info-value" style={{ fontFamily: 'monospace' }}>{baseInfo.alipayTradeId || order.paymentInfo?.alipayTradeId}</span>
                </div>
              )}
            </div>

            {/* Payment Breakdown */}
            <div className="cb-info-block" style={{ marginTop: '20px' }}>
              <h4>Payment Breakdown</h4>
              <div className="cb-detail-info-row">
                <span className="cb-detail-info-label">Product Total:</span>
                <span className="cb-detail-info-value">{formatPrice(order.sourceSumProductPayment ?? baseInfo.sumProductPayment ?? order.paymentInfo?.sumProductPayment ?? 0)}</span>
              </div>
              <div className="cb-detail-info-row">
                <span className="cb-detail-info-label">Shipping Fee:</span>
                <span className="cb-detail-info-value">{formatPrice(baseInfo.shippingFee || order.paymentInfo?.shippingFee || 0)}</span>
              </div>
              {(baseInfo.discount > 0 || order.paymentInfo?.discount > 0) && (
                <div className="cb-detail-info-row">
                  <span className="cb-detail-info-label">Discount:</span>
                  <span className="cb-detail-info-value" style={{ color: '#10b981' }}>-{formatPrice(baseInfo.discount || order.paymentInfo?.discount)}</span>
                </div>
              )}
              {(baseInfo.couponFee > 0 || order.paymentInfo?.couponFee > 0) && (
                <div className="cb-detail-info-row">
                  <span className="cb-detail-info-label">Coupon:</span>
                  <span className="cb-detail-info-value" style={{ color: '#10b981' }}>-{formatPrice(baseInfo.couponFee || order.paymentInfo?.couponFee)}</span>
                </div>
              )}
              {(baseInfo.refund > 0 || order.paymentInfo?.refund > 0) && (
                <div className="cb-detail-info-row">
                  <span className="cb-detail-info-label">Refund:</span>
                  <span className="cb-detail-info-value" style={{ color: '#dc2626' }}>{formatPrice(baseInfo.refund || order.paymentInfo?.refund)}</span>
                </div>
              )}
              <div className="cb-detail-info-row total" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px', marginTop: '12px' }}>
                <span className="cb-detail-info-label" style={{ fontWeight: 600 }}>Total Amount:</span>
                <span className="cb-detail-info-value" style={{ fontWeight: 600, color: '#ff6600', fontSize: '16px' }}>
                  {formatPrice(sourceRmb != null ? effectiveToPayRmb : (baseInfo.totalAmount || order.paymentInfo?.totalAmount || order.totalAmount))}
                </span>
              </div>
            </div>

            {/* Payment Channels */}
            {(baseInfo.payChannelList?.length > 0 || order.paymentInfo?.payChannelList?.length > 0) && (
              <div className="cb-info-block" style={{ marginTop: '20px' }}>
                <h4>Payment Channels</h4>
                <div className="cb-detail-info-row">
                  <span className="cb-detail-info-label">Payment Method:</span>
                  <span className="cb-detail-info-value">
                    {(baseInfo.payChannelList || order.paymentInfo?.payChannelList || []).join(', ')}
                  </span>
                </div>
              </div>
            )}

            {/* Step Payments (for staged payments) */}
            {(baseInfo.newStepOrderList?.length > 0 || order.paymentInfo?.stepPayments?.length > 0) && (
              <div className="cb-info-block" style={{ marginTop: '20px' }}>
                <h4>Payment Steps</h4>
                {(baseInfo.newStepOrderList || order.paymentInfo?.stepPayments || []).map((step, idx) => (
                  <div key={idx} style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px', marginBottom: '8px' }}>
                    <div className="cb-detail-info-row">
                      <span className="cb-detail-info-label">Step {step.stepNo}:</span>
                      <span className="cb-detail-info-value" style={{ fontWeight: 500 }}>{step.stepName}</span>
                    </div>
                    <div className="cb-detail-info-row">
                      <span className="cb-detail-info-label">Amount:</span>
                      <span className="cb-detail-info-value">{formatPrice(step.payFee || step.paidFee)}</span>
                    </div>
                    <div className="cb-detail-info-row">
                      <span className="cb-detail-info-label">Status:</span>
                      <span className="cb-detail-info-value">
                        <span className={`payment-status ${step.payStatus === 2 ? 'paid' : 'pending'}`}>
                          {step.payStatus === 2 ? 'Paid' : 'Pending'}
                        </span>
                      </span>
                    </div>
                    {step.gmtPay && (
                      <div className="cb-detail-info-row">
                        <span className="cb-detail-info-label">Paid At:</span>
                        <span className="cb-detail-info-value">{step.gmtPay}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}
